import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {
  CURRENCY_OPTIONS,
  DEFAULT_QUANTITY_BREAK_TIERS,
  FACTOR_KIND_ORDER,
  FactorKind,
  MAX_QTY_SENTINEL,
  RateConfig,
  RateConfigDocument,
  RateFactor
} from "../../../../model/rate/RateConfig";
import {LanguagePipe} from "../../../pipe/language.pipe";
import {computeFactorId} from "../../../../model/rate/RateFactorId";
import {ValidationResult} from "../../../../model/rate/RateConfigValidator";
import {EMPTY_RESULT, exactErrorAt, subtreeIssues} from "../validation-paths";

/**
 * Top-level body of one scope (e.g. SHORT_RUN). Renders:
 *  - scope-level controls: currency, percentageDiscount, minimums,
 *    defaultQuantityBreaks (the tier list that drives quantityBreak
 *    propertyCharacteristic options in groups);
 *  - one collapsible {@link KindSectionComponent} per FactorKind in
 *    {@link FACTOR_KIND_ORDER}, each listing its factors and offering
 *    "Add factor" affordance.
 *
 * All mutations bubble up via `(configChange)` carrying the full new
 * scope config; the parent rate-config-editor reapplies it to the
 * canonical document and runs validation.
 */
@Component({
  selector: 'app-rate-scope-panel',
  templateUrl: './scope-panel.component.html',
  styleUrls: ['./scope-panel.component.css']
})
export class ScopePanelComponent implements OnChanges {
  @Input() scope: string = '';
  @Input() config: Partial<RateConfig> = {};
  @Input() doc: RateConfigDocument = {};
  /** Last-saved snapshot of the document; passed through to the preview matrix
   *  so it can show price-change deltas vs. the baseline. */
  @Input() baselineDoc: RateConfigDocument = {};
  /** Companion-cost document for the rates preview (e.g. the SP's
   *  `serviceProviderChargedRateConfig` when editing SP `rateConfig`). */
  @Input() companionDoc: RateConfigDocument = {};
  /** Human-readable label for the companion document (e.g. "network cost"). */
  @Input() companionLabel: string = '';
  /**
   * Peer documents to compare against in the Rates Preview, side-by-side
   * with the current rate. Used by the Network-Settings SP `rateConfig`
   * editor to surface each selected facility's rate alongside the SP rate
   * for every preview cell. Pass-through to the preview-matrix child.
   */
  @Input() peerDocs: { label: string; doc: RateConfigDocument }[] = [];
  /** Catalog the preview's facility multi-select picks from. */
  @Input() availableFacilities: { id: number; label: string }[] = [];
  /** Currently-picked facility ids for the preview's peer comparison. */
  @Input() selectedFacilityIds: number[] = [];
  /** Whether the companion (e.g. network-cost) doc is rendered as a peer. */
  @Input() showCompanionAsPeer: boolean = true;
  @Output() readonly selectedFacilityIdsChange = new EventEmitter<number[]>();
  @Output() readonly showCompanionAsPeerChange = new EventEmitter<boolean>();
  @Input() readOnly: boolean = false;
  @Input() currency: string = 'USD';
  @Input() validation: ValidationResult = EMPTY_RESULT;

  /**
   * True when the document being edited is a packing rate-config variant.
   * Drives two pieces of UI gating:
   *  - the `PER_PACKAGE` {@link FactorKind} section is only listed when
   *    this is `true` (production rate-configs never see a `packageTypes[]`
   *    input, so `PER_PACKAGE` factors would silently contribute 0 there
   *    and only confuse the operator); see {@link visibleFactorKinds}.
   *  - forwarded to the {@link PreviewMatrixComponent} so the order-level
   *    "Packing preview" controls only render against packing documents.
   *
   * Defaulted to `false` so production-side hosts that omit the binding
   * (and the legacy preview-bare scope panel) keep their previous
   * production-only behavior.
   */
  @Input() isPackingConfig: boolean = false;

  @Output() configChange = new EventEmitter<Partial<RateConfig>>();
  /**
   * Page-counts edits live on the document (`doc._previewPageCounts`), not on
   * the scope config. The preview-matrix child emits its edits here; we
   * passthrough to the editor so it can mutate the canonical document.
   */
  @Output() docPageCountsChange = new EventEmitter<number[] | undefined>();

  /** Localized number formatter exposed for templates. */
  readonly localizeNumber = (n: any) => LanguagePipe.localizeNumber(n);

  /** Localized number parser exposed for templates. */
  readonly delocalizeNumber = (s: string) => LanguagePipe.delocalizeNumber(s);

  /**
   * Factor-kind sections to render in the scope body. Production rate-
   * configs hide `PER_PACKAGE` because order-level packing iteration
   * is only meaningful for packing documents; including it on
   * production scopes confused operators by suggesting the kind would
   * contribute to per-item production pricing (it doesn't — the
   * evaluator silently contributes 0 for PER_PACKAGE on a per-item
   * call). Packing configs keep the full list.
   *
   * Computed lazily into a cached field so the template's `*ngFor`
   * sees a stable array reference across change-detection cycles —
   * returning a fresh `.filter()` array from a getter on every CD
   * pass re-keys the `*ngFor` items and rebuilds every
   * `<app-rate-kind-section>` per tick.
   */
  visibleFactorKinds: FactorKind[] = FACTOR_KIND_ORDER.filter(k => k !== 'PER_PACKAGE');

  private refreshVisibleFactorKinds(): void {
    this.visibleFactorKinds = this.isPackingConfig
      ? FACTOR_KIND_ORDER.slice()
      : FACTOR_KIND_ORDER.filter(k => k !== 'PER_PACKAGE');
  }

  readonly currencyOptions = CURRENCY_OPTIONS;

  /**
   * Local copy of the tier-list rows being edited. Only the min is editable;
   * `maxStr` is derived (next row's min − 1, or ∞ for the last tier) and shown
   * read-only so adjacent tiers always remain contiguous integer ranges.
   *
   * `id` is a monotonically-incremented per-row stable identity used by
   * {@link trackTierRow}. We MUST keep DOM rows stable across the
   * config→commit→config-update round-trip; otherwise `*ngFor` destroys and
   * re-creates the `<input>` after every keystroke and steals focus.
   */
  tierRows: { id: number; minStr: string; maxStr: string }[] = [];
  private nextTierRowId: number = 1;

  /**
   * JSON snapshot of the most recent `defaultQuantityBreaks` we emitted via
   * {@link commitTiers}. When `ngOnChanges` fires with config carrying
   * exactly this snapshot, we skip {@link refreshTierRows} so the user's
   * in-progress text edits aren't clobbered (and the `<input>` doesn't
   * lose focus from the rebuild).
   */
  private lastEmittedTiersJson: string = '';

  /** Whether the scope-settings panel is currently expanded. Defaults to
   *  collapsed so the Rates Preview is the only section open on first
   *  open of the RateConfig UI; the user can expand on demand. */
  scopeControlsExpanded: boolean = false;
  /** Whether the (infrequently changed) tier list is shown. Hidden by default. */
  tiersExpanded: boolean = false;

  /**
   * Per-kind cached slices of `config.factors[]`. Recomputed only when
   * `config` actually changes (via `ngOnChanges`) so the template bindings
   * `[factors]="factorsByKind[kind]"` are referentially stable across
   * change-detection cycles. Returning a fresh array from a getter/method
   * on every CD pass caused an infinite CD loop in the child sections
   * (each new array reference re-marked KindSection dirty).
   */
  factorsByKind: { [k in FactorKind]?: RateFactor[] } = {};
  /** Per-kind cached absolute-index arrays, same rationale as `factorsByKind`. */
  factorAbsoluteIndexesByKind: { [k in FactorKind]?: number[] } = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.refreshTierRows();
      this.refreshFactorIndexes();
    }
    if (changes['isPackingConfig']) {
      this.refreshVisibleFactorKinds();
    }
  }

  private refreshFactorIndexes(): void {
    const list = this.config.factors || [];
    const byKind: { [k in FactorKind]?: RateFactor[] } = {};
    const idxByKind: { [k in FactorKind]?: number[] } = {};
    for (const k of FACTOR_KIND_ORDER) { byKind[k] = []; idxByKind[k] = []; }
    for (let i = 0; i < list.length; i++) {
      const k = list[i].kind;
      if (!byKind[k]) { byKind[k] = []; idxByKind[k] = []; }
      byKind[k]!.push(list[i]);
      idxByKind[k]!.push(i);
    }
    this.factorsByKind = byKind;
    this.factorAbsoluteIndexesByKind = idxByKind;
  }

  private refreshTierRows(): void {
    const tiers = (this.config && this.config.defaultQuantityBreaks && this.config.defaultQuantityBreaks.length > 0)
      ? this.config.defaultQuantityBreaks
      : [];
    // If the inbound config carries exactly the tiers we just emitted, the
    // change came from our own commitTiers() round-trip. Don't rebuild the
    // local rows — that would discard the focused <input> mid-edit.
    const inboundJson = JSON.stringify(tiers);
    if (inboundJson === this.lastEmittedTiersJson && this.tierRows.length > 0) {
      return;
    }
    this.tierRows = tiers.map(t => ({
      id: this.nextTierRowId++,
      minStr: this.localizeNumber(t.minQty),
      maxStr: t.maxQty >= MAX_QTY_SENTINEL ? '' : this.localizeNumber(t.maxQty)
    }));
  }

  /** Stable identity for the tier-row *ngFor, preserves the focused input across edits. */
  trackTierRow = (_index: number, row: { id: number }) => row.id;

  /** Factors of one kind, in document order. Cached via {@link factorsByKind}. */
  factorsOfKind(kind: FactorKind): RateFactor[] {
    return this.factorsByKind[kind] || [];
  }

  /**
   * For each factor of the given kind, the absolute index into
   * `config.factors[]`. Cached via {@link factorAbsoluteIndexesByKind}.
   */
  factorAbsoluteIndexes(kind: FactorKind): number[] {
    return this.factorAbsoluteIndexesByKind[kind] || [];
  }

  /** Validator path prefix for this scope. */
  get scopePath(): string { return `/${this.scope}`; }

  /** Issue counts within the kind-section subtree (sum across that kind's factors). */
  kindIssues(kind: FactorKind): { errors: number; warnings: number } {
    const indexes = this.factorAbsoluteIndexes(kind);
    let errors = 0;
    let warnings = 0;
    for (const i of indexes) {
      const sub = subtreeIssues(this.validation, `${this.scopePath}/factors/${i}`);
      errors += sub.errors.length;
      warnings += sub.warnings.length;
    }
    return {errors, warnings};
  }

  /** Issue counts attached directly to scope-level fields (currency, percentageDiscount, defaultQuantityBreaks, version). */
  get scopeLevelIssueCount(): number {
    const sub = subtreeIssues(this.validation, this.scopePath);
    // Count issues NOT under /factors/* — those live in the kind sections.
    const factorsPrefix = `${this.scopePath}/factors`;
    return sub.errors.filter(e => e.path.indexOf(factorsPrefix) !== 0).length;
  }

  /**
   * True when the validator reports an error at the persisted index for the
   * tier row at `i`. Persisted indexes follow `commitTiers` ordering — rows
   * whose `min` doesn't parse to a finite number are skipped at commit time
   * and therefore have no validator path either, so the highlight stays off
   * mid-edit until the user finishes typing.
   */
  tierRowHasError(i: number): boolean {
    const persistedIndex = this.persistedTierIndexFor(i);
    if (persistedIndex < 0) { return false; }
    return exactErrorAt(this.validation, `${this.scopePath}/defaultQuantityBreaks/${persistedIndex}`);
  }

  /**
   * Map an editor-row index to its index in the committed
   * `defaultQuantityBreaks[]`. Returns -1 when the row was skipped at commit
   * time (e.g. min field is empty / not yet a number).
   */
  private persistedTierIndexFor(rowIndex: number): number {
    let persisted = 0;
    for (let j = 0; j <= rowIndex && j < this.tierRows.length; j++) {
      const minN = this.delocalizeNumber(this.tierRows[j].minStr);
      if (!isFinite(minN)) {
        if (j === rowIndex) { return -1; }
        continue;
      }
      // For non-last rows commit also requires the next row's min to parse.
      if (j < this.tierRows.length - 1) {
        const nextMin = this.delocalizeNumber(this.tierRows[j + 1].minStr);
        if (!isFinite(nextMin)) {
          if (j === rowIndex) { return -1; }
          continue;
        }
      }
      if (j === rowIndex) { return persisted; }
      persisted++;
    }
    return -1;
  }

  // ── Scope-level mutations ────────────────────────────────────────

  setCurrency(value: string): void {
    if (this.readOnly) { return; }
    this.emit({...this.config, currency: value});
  }

  setPercentageDiscountStr(raw: string): void {
    if (this.readOnly) { return; }
    const next: Partial<RateConfig> = {...this.config};
    if (raw == null || raw === '') {
      delete next.percentageDiscount;
    } else {
      const n = this.delocalizeNumber(raw);
      if (isFinite(n)) { next.percentageDiscount = n; }
    }
    this.emit(next);
  }

  setMinimumPerItemStr(raw: string): void {
    if (this.readOnly) { return; }
    const next: Partial<RateConfig> = {...this.config, minimums: {...(this.config.minimums || {})}};
    if (raw == null || raw === '') {
      delete next.minimums!.perItem;
    } else {
      const n = this.delocalizeNumber(raw);
      if (isFinite(n)) { next.minimums!.perItem = n; }
    }
    if (Object.keys(next.minimums || {}).length === 0) { delete next.minimums; }
    this.emit(next);
  }

  setMinimumPerOrderStr(raw: string): void {
    if (this.readOnly) { return; }
    const next: Partial<RateConfig> = {...this.config, minimums: {...(this.config.minimums || {})}};
    if (raw == null || raw === '') {
      delete next.minimums!.perOrder;
    } else {
      const n = this.delocalizeNumber(raw);
      if (isFinite(n)) { next.minimums!.perOrder = n; }
    }
    if (Object.keys(next.minimums || {}).length === 0) { delete next.minimums; }
    this.emit(next);
  }

  // ── Tier list (defaultQuantityBreaks) ─────────────────────────────

  /**
   * Insert a new tier row at `position` (0..tierRows.length). The new row's
   * min is seeded as midway between the surrounding tiers, or "previous + 1"
   * when appended, or "1" when prepended into an empty list. Caller does
   * not need to specify a max — it stays derived from the next row's min.
   */
  insertTierRow(position: number): void {
    if (this.readOnly) { return; }
    const N = this.tierRows.length;
    const safePos = Math.max(0, Math.min(position, N));
    let seedMin = '';
    const prev = safePos > 0 ? this.delocalizeNumber(this.tierRows[safePos - 1].minStr) : NaN;
    const next = safePos < N ? this.delocalizeNumber(this.tierRows[safePos].minStr) : NaN;
    if (isFinite(prev) && isFinite(next) && next - prev >= 2) {
      // Insert between two existing tiers — pick the midpoint.
      seedMin = this.localizeNumber(Math.floor((prev + next) / 2));
    } else if (isFinite(prev) && !isFinite(next)) {
      // Append after the last tier.
      seedMin = this.localizeNumber(prev + 1);
    } else if (!isFinite(prev) && isFinite(next) && next > 1) {
      // Prepend before the first tier — start at 1 if there's room.
      seedMin = this.localizeNumber(1);
    } else if (N === 0) {
      seedMin = this.localizeNumber(1);
    }
    const row = {id: this.nextTierRowId++, minStr: seedMin, maxStr: ''};
    this.tierRows.splice(safePos, 0, row);
    this.recomputeTierMaxes();
    this.commitTiers();
  }

  /**
   * Seed the table with the engine defaults ({@link DEFAULT_QUANTITY_BREAK_TIERS}).
   * Used by the empty-state action to give the user a sensible starting point
   * to edit, rather than forcing them to build the table from scratch.
   */
  seedDefaultTiers(): void {
    if (this.readOnly) { return; }
    this.tierRows = DEFAULT_QUANTITY_BREAK_TIERS.map(t => ({
      id: this.nextTierRowId++,
      minStr: this.localizeNumber(t.minQty),
      maxStr: t.maxQty >= MAX_QTY_SENTINEL ? '' : this.localizeNumber(t.maxQty)
    }));
    this.commitTiers();
  }

  /** @deprecated Use {@link insertTierRow} via the per-row "+ insert" affordances. */
  addTierRow(): void {
    this.insertTierRow(this.tierRows.length);
  }

  removeTierRow(idx: number): void {
    if (this.readOnly) { return; }
    this.tierRows.splice(idx, 1);
    this.recomputeTierMaxes();
    this.commitTiers();
  }

  setTierMin(idx: number, raw: string): void {
    if (this.readOnly || idx < 0 || idx >= this.tierRows.length) { return; }
    this.tierRows[idx].minStr = raw;
    this.recomputeTierMaxes();
    this.commitTiers();
  }

  /**
   * Walk the rows in order; each row's max becomes `(next row's min) − 1`,
   * except the last row which is always ∞ (rendered separately in the
   * template). Skips rows whose min is not yet a finite number.
   */
  private recomputeTierMaxes(): void {
    for (let i = 0; i < this.tierRows.length; i++) {
      if (i === this.tierRows.length - 1) {
        this.tierRows[i].maxStr = '';
        continue;
      }
      const nextMin = this.delocalizeNumber(this.tierRows[i + 1].minStr);
      if (isFinite(nextMin)) {
        this.tierRows[i].maxStr = this.localizeNumber(nextMin - 1);
      } else {
        this.tierRows[i].maxStr = '';
      }
    }
  }

  /** Parse the row strings into tiers; ignore obviously bad rows so the user
   * can keep typing without an "Invalid" yank between keystrokes. */
  private commitTiers(): void {
    const tiers: { minQty: number; maxQty: number }[] = [];
    const N = this.tierRows.length;
    for (let i = 0; i < N; i++) {
      const minN = this.delocalizeNumber(this.tierRows[i].minStr);
      if (!isFinite(minN)) { continue; }
      let maxN: number;
      if (i === N - 1) {
        maxN = MAX_QTY_SENTINEL;
      } else {
        const nextMin = this.delocalizeNumber(this.tierRows[i + 1].minStr);
        if (!isFinite(nextMin)) { continue; }
        maxN = nextMin - 1;
      }
      tiers.push({minQty: minN, maxQty: maxN});
    }
    const next: Partial<RateConfig> = {...this.config};
    if (tiers.length > 0) { next.defaultQuantityBreaks = tiers; }
    else { delete next.defaultQuantityBreaks; }
    // Snapshot what we're about to emit so the upcoming ngOnChanges round-trip
    // can identify it and skip the focus-stealing rebuild of `tierRows`.
    this.lastEmittedTiersJson = JSON.stringify(tiers);
    this.emit(next);
  }

  // ── Scope-settings & tier-list expansion ────────────────────────────

  onScopeControlsExpandedChange(expanded: boolean): void {
    this.scopeControlsExpanded = expanded;
  }

  toggleTiers(event: Event): void {
    // Prevent the host expansion-panel header from swallowing the click and
    // collapsing the entire scope-settings section.
    event.stopPropagation();
    this.tiersExpanded = !this.tiersExpanded;
  }

  // ── Factor add / mutation / delete ────────────────────────────────

  addFactor(kind: FactorKind): void {
    if (this.readOnly) { return; }
    // Seed all numeric fields with explicit 0 so the persisted factor matches
    // the "0 by default" display. The engine treats 0 in `perItemMinimum` /
    // `signatureRoundingMultiple` the same as undefined (no-op), so this is
    // cosmetic for the engine but consistent for the UI.
    const factor: RateFactor = {
      id: '',
      kind,
      groups: [],
      baseAmount: 0,
      perItemMinimum: 0
    };
    if (kind === 'PER_NUMERIC_PROPERTY') {
      factor.property = 'pages';
      factor.signatureRoundingMultiple = 0;
    }
    factor.id = computeFactorId(factor);
    const next: Partial<RateConfig> = {
      ...this.config,
      factors: [...(this.config.factors || []), factor]
    };
    this.emit(next);
  }

  /** A KindSection emitted a per-factor change; replace it in the list (matched by reference, then by position). */
  onFactorChange(index: number, updated: RateFactor): void {
    if (this.readOnly) { return; }
    const list = (this.config.factors || []).slice();
    if (index >= 0 && index < list.length) {
      // Recompute id since the factor's signature may have changed.
      const fixed: RateFactor = {...updated, id: ''};
      fixed.id = computeFactorId(fixed);
      list[index] = fixed;
    }
    this.emit({...this.config, factors: list});
  }

  onFactorDelete(index: number): void {
    if (this.readOnly) { return; }
    const list = (this.config.factors || []).slice();
    if (index >= 0 && index < list.length) { list.splice(index, 1); }
    this.emit({...this.config, factors: list});
  }

  /**
   * Translate a per-kind index into the absolute index in
   * `config.factors[]`. The KindSection emits per-kind indexes; the
   * scope panel rewires them so it can splice the canonical list.
   */
  resolveAbsoluteIndex(kind: FactorKind, perKindIndex: number): number {
    let absolute = -1;
    let perKind = -1;
    const list = this.config.factors || [];
    for (let i = 0; i < list.length; i++) {
      if (list[i].kind !== kind) { continue; }
      perKind++;
      if (perKind === perKindIndex) { absolute = i; break; }
    }
    return absolute;
  }

  onKindFactorChange(kind: FactorKind, perKindIndex: number, updated: RateFactor): void {
    const absolute = this.resolveAbsoluteIndex(kind, perKindIndex);
    if (absolute >= 0) { this.onFactorChange(absolute, updated); }
  }

  onKindFactorDelete(kind: FactorKind, perKindIndex: number): void {
    const absolute = this.resolveAbsoluteIndex(kind, perKindIndex);
    if (absolute >= 0) { this.onFactorDelete(absolute); }
  }

  private emit(next: Partial<RateConfig>): void {
    // Always re-stamp version so an empty scope acquires v3.
    if (next.version !== 3) { next.version = 3; }
    if (!next.currency) { next.currency = this.currency || 'USD'; }
    if (!Array.isArray(next.factors)) { next.factors = []; }
    this.configChange.emit(next);
  }

  /** Passthrough from {@link PreviewMatrixComponent.pageCountsChange}. */
  onPageCountsChange(next: number[] | undefined): void {
    this.docPageCountsChange.emit(next);
  }
}

